import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import Decimal from 'decimal.js';
import type {
  Account,
  Class,
  Contact,
  CreateJournalEntryInput,
  JournalEntryWithLines,
  Location,
} from '@accounting/shared';
import { useAccounts } from '../../hooks/useAccounts';
import { useCreateJournalEntry, useUpdateJournalEntry } from '../../hooks/useJournalEntries';
import { useContacts } from '../../hooks/useContacts';
import { useClasses, useLocations } from '../../hooks/useDimensions';
import { ApiError } from '../../lib/api';
import { formatCurrency, todayIso } from '../../lib/formatters';
import { AccountPicker } from '../accounts/AccountPicker';

interface LineDraft {
  id: string;
  accountId: string;
  debit: string;
  credit: string;
  memo: string;
  contactId: string;
  locationId: string;
  classId: string;
}

let nextId = 1;
const newLine = (): LineDraft => ({
  id: `l${nextId++}`,
  accountId: '',
  debit: '',
  credit: '',
  memo: '',
  contactId: '',
  locationId: '',
  classId: '',
});

interface Props {
  companyId: string;
  /** When provided (copy/duplicate), seed the form from this entry (date resets to today). */
  initialEntry?: JournalEntryWithLines;
  /** When set, edit this manual entry in place (keeps its date and entry number). */
  editEntryId?: string;
  /**
   * When true, send override=true on the update — bypasses the reconciliation
   * lock and the period lock. Requires overrideReason (a small textarea appears).
   * Set by the JE edit page when the entry is locked.
   */
  forceOverride?: boolean;
}

const sideValue = (v: string) => (parseFloat(v) > 0 ? String(parseFloat(v)) : '');

export function JournalEntryForm({ companyId, initialEntry, editEntryId, forceOverride }: Props) {
  const navigate = useNavigate();
  const { data: accounts = [], isLoading: accountsLoading } = useAccounts(companyId);
  const { data: contacts = [] } = useContacts(companyId);
  const { data: locations = [] } = useLocations(companyId);
  const { data: classes = [] } = useClasses(companyId);
  const create = useCreateJournalEntry(companyId);
  const update = useUpdateJournalEntry(companyId);
  const isEdit = !!editEntryId;
  const mutation = isEdit ? update : create;

  const [date, setDate] = useState(isEdit && initialEntry ? initialEntry.date : todayIso());
  const [memo, setMemo] = useState(initialEntry?.memo ?? '');
  const [reference, setReference] = useState(initialEntry?.reference ?? '');
  const [overrideReason, setOverrideReason] = useState('');
  const [lines, setLines] = useState<LineDraft[]>(() =>
    initialEntry
      ? initialEntry.lines.map((l) => ({
          id: `l${nextId++}`,
          accountId: l.accountId,
          debit: sideValue(l.debit),
          credit: sideValue(l.credit),
          memo: l.memo ?? '',
          contactId: l.contactId ?? '',
          locationId: l.locationId ?? '',
          classId: l.classId ?? '',
        }))
      : [newLine(), newLine()],
  );

  const updateLine = (id: string, patch: Partial<LineDraft>) =>
    setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const removeLine = (id: string) =>
    setLines((ls) => (ls.length <= 2 ? ls : ls.filter((l) => l.id !== id)));
  const addLine = () => setLines((ls) => [...ls, newLine()]);

  const totals = useMemo(() => {
    let debits = new Decimal(0);
    let credits = new Decimal(0);
    for (const l of lines) {
      if (l.debit) {
        try { debits = debits.plus(l.debit); } catch { /* ignore parse errors */ }
      }
      if (l.credit) {
        try { credits = credits.plus(l.credit); } catch { /* ignore parse errors */ }
      }
    }
    return { debits, credits, diff: debits.minus(credits) };
  }, [lines]);

  const balanced = totals.diff.isZero() && !totals.debits.isZero();

  const linesValid = lines.every((l) => {
    if (!l.accountId) return false;
    const d = l.debit ? new Decimal(l.debit) : new Decimal(0);
    const c = l.credit ? new Decimal(l.credit) : new Decimal(0);
    if (d.isNegative() || c.isNegative()) return false;
    if (d.isZero() && c.isZero()) return false;
    if (d.greaterThan(0) && c.greaterThan(0)) return false;
    return true;
  });

  const canSubmit =
    balanced && linesValid && !!date && !mutation.isPending &&
    // Override edits require a written reason for the audit log.
    (!forceOverride || overrideReason.trim().length > 0);

  // "Post & New": post the entry, then reset the form for the next one instead of navigating.
  const andNewRef = useRef(false);
  const [flash, setFlash] = useState<string | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);

  const resetForNext = () => {
    setMemo('');
    setReference('');
    setLines([newLine(), newLine()]);
    setFlash('Entry posted — ready for the next one.');
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), 4000);
    // The date is kept so several same-day entries are quick to post in a row.
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const andNew = andNewRef.current;
    andNewRef.current = false;
    setFlash(null);
    const builtLines = lines.map((l) => ({
      accountId: l.accountId,
      debit: l.debit || '0',
      credit: l.credit || '0',
      memo: l.memo.trim() || null,
      contactId: l.contactId || null,
      locationId: l.locationId || null,
      classId: l.classId || null,
    }));

    if (isEdit && editEntryId) {
      update.mutate(
        {
          entryId: editEntryId,
          input: {
            date,
            memo: memo.trim() || null,
            reference: reference.trim() || null,
            lines: builtLines,
            ...(forceOverride
              ? { override: true, overrideReason: overrideReason.trim() }
              : {}),
          },
        },
        { onSuccess: () => void navigate({ to: '/journal/$id', params: { id: editEntryId } }) },
      );
      return;
    }

    const input: CreateJournalEntryInput = {
      date,
      memo: memo.trim() || null,
      source: 'manual',
      reference: reference.trim() || null,
      lines: builtLines,
    };
    create.mutate(input, {
      onSuccess: () => {
        if (andNew) resetForNext();
        else void navigate({ to: '/journal' });
      },
    });
  };

  const apiError = mutation.error;
  const errorBody = apiError instanceof ApiError ? (apiError.body as Record<string, unknown> | null) : null;
  const errorMessage =
    typeof errorBody?.error === 'string'
      ? errorBody.error
      : apiError?.message;

  return (
    <form onSubmit={submit}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr max-content 1fr',
          gap: '12px 16px',
          alignItems: 'center',
          marginBottom: 16,
          maxWidth: 800,
        }}
      >
        <label htmlFor="date">Date</label>
        <input
          id="date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          required
        />

        <label htmlFor="ref">Reference</label>
        <input
          id="ref"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="(optional)"
        />

        <label htmlFor="memo" style={{ gridColumn: '1' }}>Memo</label>
        <input
          id="memo"
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          style={{ gridColumn: '2 / span 3' }}
        />

        {forceOverride && (
          <>
            <label htmlFor="override-reason" style={{ gridColumn: '1', color: 'var(--tag-warn-fg)', fontWeight: 600 }}>
              Override reason
            </label>
            <input
              id="override-reason"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              required
              placeholder="Why are you editing a locked entry? (audited)"
              style={{ gridColumn: '2 / span 3' }}
            />
          </>
        )}
      </div>

      <table className="table" style={{ marginBottom: 12 }}>
        <thead>
          <tr>
            <th style={{ width: '32%' }}>Account</th>
            <th className="right" style={{ width: 130 }}>Debit</th>
            <th className="right" style={{ width: 130 }}>Credit</th>
            <th>Memo &amp; dimensions</th>
            <th style={{ width: 60 }}></th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <LineRow
              key={line.id}
              line={line}
              accounts={accounts ?? []}
              accountsLoading={accountsLoading}
              contacts={contacts}
              locations={locations}
              classes={classes}
              onChange={(patch) => updateLine(line.id, patch)}
              onRemove={() => removeLine(line.id)}
              canRemove={lines.length > 2}
            />
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--text-muted)' }}>Totals</td>
            <td className="right" style={{ fontWeight: 600 }}>
              {formatCurrency(totals.debits.toFixed(4))}
            </td>
            <td className="right" style={{ fontWeight: 600 }}>
              {formatCurrency(totals.credits.toFixed(4))}
            </td>
            <td colSpan={2}>
              {totals.diff.isZero() ? (
                totals.debits.isZero() ? (
                  <span className="muted">enter amounts</span>
                ) : (
                  <span className="tag ok">balanced</span>
                )
              ) : (
                <span className="tag bad">
                  off by {formatCurrency(totals.diff.abs().toFixed(4))}
                </span>
              )}
            </td>
          </tr>
        </tfoot>
      </table>

      <button type="button" className="btn-secondary" onClick={addLine}>
        + Add line
      </button>

      {errorMessage && (
        <div className="field-error" style={{ marginTop: 16 }}>
          {errorMessage}
          {typeof errorBody?.debits === 'string' && typeof errorBody?.credits === 'string' && (
            <div style={{ marginTop: 4 }}>
              server saw debits {formatCurrency(errorBody.debits as string)} / credits{' '}
              {formatCurrency(errorBody.credits as string)}
            </div>
          )}
        </div>
      )}

      {flash && (
        <div className="tag ok" style={{ marginTop: 16, display: 'inline-block' }}>
          {flash}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 24, alignItems: 'center' }}>
        <button type="submit" className="btn-primary" disabled={!canSubmit}>
          {isEdit
            ? mutation.isPending
              ? 'Saving…'
              : 'Save changes'
            : mutation.isPending
              ? 'Posting…'
              : 'Post entry'}
        </button>
        {!isEdit && (
          <button
            type="submit"
            className="btn-secondary"
            disabled={!canSubmit}
            onClick={() => {
              andNewRef.current = true;
            }}
          >
            Post &amp; New
          </button>
        )}
        <button
          type="button"
          className="btn-secondary"
          onClick={() =>
            void navigate(
              isEdit && editEntryId
                ? { to: '/journal/$id', params: { id: editEntryId } }
                : { to: '/journal' },
            )
          }
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function LineRow({
  line,
  accounts,
  accountsLoading,
  contacts,
  locations,
  classes,
  onChange,
  onRemove,
  canRemove,
}: {
  line: LineDraft;
  accounts: Account[];
  accountsLoading: boolean;
  contacts: Contact[];
  locations: Location[];
  classes: Class[];
  onChange: (patch: Partial<LineDraft>) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const hasAnyDimension = !!(contacts.length || locations.length || classes.length);
  return (
    <tr>
      <td>
        <AccountPicker
          accounts={accounts}
          value={line.accountId}
          onChange={(id) => onChange({ accountId: id })}
          predicate={(a) => a.isActive && a.allowDirectPosting}
          disabled={accountsLoading}
          required
        />
      </td>
      <td>
        <input
          type="text"
          inputMode="decimal"
          value={line.debit}
          onChange={(e) =>
            onChange({
              debit: e.target.value,
              credit: e.target.value ? '' : line.credit,
            })
          }
          placeholder="0.00"
          style={{ width: '100%', textAlign: 'right' }}
        />
      </td>
      <td>
        <input
          type="text"
          inputMode="decimal"
          value={line.credit}
          onChange={(e) =>
            onChange({
              credit: e.target.value,
              debit: e.target.value ? '' : line.debit,
            })
          }
          placeholder="0.00"
          style={{ width: '100%', textAlign: 'right' }}
        />
      </td>
      <td>
        <input
          type="text"
          value={line.memo}
          onChange={(e) => onChange({ memo: e.target.value })}
          style={{ width: '100%' }}
          placeholder="memo (optional)"
        />
        {hasAnyDimension && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 6,
              marginTop: 6,
            }}
          >
            {contacts.length > 0 && (
              <select
                value={line.contactId}
                onChange={(e) => onChange({ contactId: e.target.value })}
                style={{ width: '100%', fontSize: 13 }}
                title="Contact"
              >
                <option value="">— contact —</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
            {locations.length > 0 && (
              <select
                value={line.locationId}
                onChange={(e) => onChange({ locationId: e.target.value })}
                style={{ width: '100%', fontSize: 13 }}
                title="Location"
              >
                <option value="">— location —</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            )}
            {classes.length > 0 && (
              <select
                value={line.classId}
                onChange={(e) => onChange({ classId: e.target.value })}
                style={{ width: '100%', fontSize: 13 }}
                title="Class"
              >
                <option value="">— class —</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
      </td>
      <td className="right">
        <button
          type="button"
          className="btn-danger"
          onClick={onRemove}
          disabled={!canRemove}
          title={canRemove ? 'Remove line' : 'Minimum 2 lines required'}
        >
          ×
        </button>
      </td>
    </tr>
  );
}
